const DEFAULT_LIFE_BY_SEVERITY = Object.freeze({
    success: 2800,
    info: 3400,
    warn: 4600,
    error: 5600,
    secondary: 3400,
    contrast: 3400,
})

const DEFAULT_GROUP = '__default__'

const asArray = (value) => (Array.isArray(value) ? value : [value]).filter(Boolean)

const stringify = (value) => {
    if (value === undefined || value === null) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return `${value}`
    try {
        return JSON.stringify(value)
    } catch (error) {
        return String(value)
    }
}

const getGroup = (message) => message?.group ?? DEFAULT_GROUP

const getDedupKey = (message) =>
    [getGroup(message), message?.severity ?? 'info', stringify(message?.summary), stringify(message?.detail)].join('|')

export const createManagedToastService = (baseToast, options = {}) => {
    const maxVisible = Number.isFinite(options.maxVisible) ? Math.max(1, options.maxVisible) : 3
    const dedupWindowMs = Number.isFinite(options.dedupWindowMs) ? Math.max(0, options.dedupWindowMs) : 1500
    const defaultLifeBySeverity = {
        ...DEFAULT_LIFE_BY_SEVERITY,
        ...(options.defaultLifeBySeverity || {}),
    }

    let nextId = 1

    const queuedByGroup = new Map()
    const activeByGroup = new Map()
    const activeById = new Map()
    const queuedById = new Map()
    const dedupByKey = new Map()

    const getActiveSet = (group) => {
        if (!activeByGroup.has(group)) activeByGroup.set(group, new Set())
        return activeByGroup.get(group)
    }

    const getQueue = (group) => {
        if (!queuedByGroup.has(group)) queuedByGroup.set(group, [])
        return queuedByGroup.get(group)
    }

    const rememberDedup = (message) => {
        dedupByKey.set(getDedupKey(message), { id: message.id, at: Date.now() })
    }

    const isDuplicate = (message) => {
        if (!dedupWindowMs) return false

        const key = getDedupKey(message)
        const previous = dedupByKey.get(key)
        if (!previous) return false

        if (Date.now() - previous.at > dedupWindowMs) {
            dedupByKey.delete(key)
            return false
        }

        return activeById.has(previous.id) || queuedById.has(previous.id)
    }

    const normalizeMessage = (message) => {
        const normalized = { ...message }

        normalized.severity = normalized.severity ?? 'info'
        normalized.id = normalized.id ?? `app-toast-${nextId++}`

        if (normalized.summary === undefined && normalized.detail !== undefined) {
            normalized.summary = stringify(normalized.detail)
            normalized.detail = undefined
        }

        if (normalized.detail !== undefined && normalized.detail !== null && typeof normalized.detail !== 'string') {
            normalized.detail = stringify(normalized.detail)
        }

        if (normalized.closable === undefined) normalized.closable = false

        const isPersistent = normalized.persistent === true || normalized.sticky === true
        if (!isPersistent && (normalized.life === undefined || normalized.life === null)) {
            normalized.life = defaultLifeBySeverity[normalized.severity] ?? defaultLifeBySeverity.info
        }

        return normalized
    }

    const dispatchNow = (message) => {
        const group = getGroup(message)
        const activeSet = getActiveSet(group)
        activeSet.add(message.id)
        activeById.set(message.id, message)
        baseToast.add(message)
    }

    const enqueue = (message) => {
        const group = getGroup(message)
        const queue = getQueue(group)
        queue.push(message)
        queuedById.set(message.id, group)
    }

    const flushGroup = (group) => {
        const activeSet = getActiveSet(group)
        const queue = getQueue(group)

        while (activeSet.size < maxVisible && queue.length > 0) {
            const nextMessage = queue.shift()
            queuedById.delete(nextMessage.id)
            dispatchNow(nextMessage)
        }

        if (queue.length === 0) queuedByGroup.delete(group)
        if (activeSet.size === 0) activeByGroup.delete(group)
    }

    const addOne = (rawMessage) => {
        const message = normalizeMessage(rawMessage)

        if (isDuplicate(message)) return
        rememberDedup(message)

        const group = getGroup(message)
        const activeSet = getActiveSet(group)
        if (activeSet.size < maxVisible || message.bypassQueue === true) {
            dispatchNow(message)
        } else {
            enqueue(message)
        }
    }

    const removeOneFromQueue = (message) => {
        const id = message?.id
        if (!id || !queuedById.has(id)) return false

        const group = queuedById.get(id)
        const queue = queuedByGroup.get(group)
        if (!queue) return false

        const nextQueue = queue.filter((item) => item.id !== id)
        if (nextQueue.length > 0) queuedByGroup.set(group, nextQueue)
        else queuedByGroup.delete(group)
        queuedById.delete(id)
        return true
    }

    const api = {
        add(messageOrMessages) {
            asArray(messageOrMessages).forEach(addOne)
        },
        remove(messageOrMessages) {
            asArray(messageOrMessages).forEach((message) => {
                if (!removeOneFromQueue(message)) {
                    baseToast.remove(message)
                }
            })
        },
        removeGroup(group) {
            if (queuedByGroup.has(group)) {
                queuedByGroup.get(group).forEach((message) => queuedById.delete(message.id))
                queuedByGroup.delete(group)
            }
            if (activeByGroup.has(group)) {
                activeByGroup.get(group).forEach((id) => activeById.delete(id))
                activeByGroup.delete(group)
            }
            baseToast.removeGroup(group)
        },
        removeAllGroups() {
            queuedByGroup.clear()
            activeByGroup.clear()
            activeById.clear()
            queuedById.clear()
            dedupByKey.clear()
            baseToast.removeAllGroups()
        },
        onMessageClosed(message) {
            const group = getGroup(message)
            const id = message?.id
            if (id !== undefined && id !== null) {
                activeById.delete(id)
                const activeSet = activeByGroup.get(group)
                if (activeSet) {
                    activeSet.delete(id)
                    if (activeSet.size === 0) activeByGroup.delete(group)
                }

                for (const [key, value] of dedupByKey.entries()) {
                    if (value.id === id) dedupByKey.delete(key)
                }
            }

            flushGroup(group)
        },
    }

    return api
}
